import React from "react";
import {Card, Empty, theme} from "antd";
import {CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis} from "recharts";
import {useTranslation} from "react-i18next";
import {chartPalette} from "@dowty/ui";
import {buildTimelineSeries, formatAlcohol, formatNumber, formatVolume} from "./utils";
type EventTimelineCardProps = {
    titleKey: string;
    emptyKey: string;
    series: ReturnType<typeof buildTimelineSeries>["volume"];
    alcohol?: boolean;
};

const EventTimelineCard: React.FC<EventTimelineCardProps> = ({ titleKey, emptyKey, series, alcohol = false }) => {
    const { token } = theme.useToken();
    const { t } = useTranslation();
    const palette = chartPalette(document.documentElement.dataset.theme === "dark" ? "dark" : "light");

    return (
        <Card title={t(titleKey)}>
            {series.data.length > 0 ? (
                <div className="analytics-chart">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={series.data} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                        <XAxis dataKey="bucket" tick={{ fill: token.colorTextSecondary, fontSize: 12 }} tickMargin={8} />
                        <YAxis tickFormatter={(value: number) => formatNumber(value)} width={48} tick={{ fill: token.colorTextSecondary, fontSize: 12 }} tickMargin={8} />
                        <RechartsTooltip
                            contentStyle={{
                                background: token.colorBgElevated,
                                color: token.colorText,
                                borderRadius: token.borderRadiusLG,
                                borderColor: token.colorBorderSecondary,
                            }}
                            labelStyle={{ color: token.colorTextSecondary }}
                            itemStyle={{ color: token.colorText }}
                            formatter={alcohol ? (value: number) => [
                                formatAlcohol(value),
                                t("eventStats.cards.timelineTooltipAlcohol"),
                            ] : (value: number, name: string) => [formatVolume(value), name]}
                        />
                        <Legend />
                        {series.lines.map((line, index) => (
                            <Line
                                key={alcohol ? `alc-${line.key}` : line.key}
                                type="monotone"
                                dataKey={line.key}
                                name={line.name}
                                stroke={palette[index % palette.length]}
                                strokeWidth={2}
                                dot={false}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
                </div>
            ) : (
                <Empty description={t(emptyKey)} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
        </Card>
    );
};

export default EventTimelineCard;
